// ** React Imports
const React = require('react');
const { useState, useCallback, useEffect } = require('react');
const T = require('prop-types');
const { default: Styled } = require('styled-components');

// ** MUI Imports
const { default: Card } = require('@mui/material/Card');
const { default: Button } = require('@mui/material/Button');
const { default: IconButton } = require('@mui/material/IconButton');
const { default: Typography } = require('@mui/material/Typography');
const { default: CardHeader } = require('@mui/material/CardHeader');
const { default: CardContent } = require('@mui/material/CardContent');
const { default: FormControl } = require('@mui/material/FormControl');
const { default: InputAdornment } = require('@mui/material/InputAdornment');
const { default: FormControlLabel } = require('@mui/material/FormControlLabel');
const { default: FormHelperText } = require('@mui/material/FormHelperText');
const { default: ListItem } = require('@mui/material/ListItem');
const { default: ListItemText } = require('@mui/material/ListItemText');
const { default: ListItemIcon } = require('@mui/material/ListItemIcon');
const { default: DialogTitle } = require('@mui/material/DialogTitle');
const { default: DialogContent } = require('@mui/material/DialogContent');
const { default: DialogContentText } = require('@mui/material/DialogContentText');
const { default: DialogActions } = require('@mui/material/DialogActions');
const { default: Dialog } = require('@mui/material/Dialog');
const { default: VisibilityIcon } = require('@mui/icons-material/Visibility');
const { default: VisibilityOffIcon } = require('@mui/icons-material/VisibilityOff');
const { default: ClearIcon } = require('@mui/icons-material/HighlightOff');
const { default: CheckCircleIcon } = require('@mui/icons-material/CheckCircle');

const { default: Classes } = require('./styles.scss');

// ** Third Party Imports
const { Controller } = require('react-hook-form');
const { CircularProgress } = require('@mui/material');

const { useTheme } = require('@mui/material/styles');

const Fonts = require('styles/fonts.json');
const { default: Checkbox } = require('@mui/material/Checkbox');
const { useForm } = require('hooks/useForm');
const ChangePasswordFormConfig = require('./formConfig');
const FullScreenDialog = require('containers/FullScreenDialog');
const PrivacyPolicyContents = require('components/PrivacyPolicyContents');
const TosContents = require('components/TosContents');
const { useDispatch } = require('react-redux');
const { context } = require('app-context');
const { analyticsTemplates } = require('utils/analytics');
const FocusIndicator = require('components/FocusIndicator');
const LoginTextField = require('components/LoginTextField');
const { buildChangePasswordSchema } = require('utils/validation/changePassword');

const internals = {};

internals.buildDefaultValues = ({
    agreeToTerms = false
} = {}) => ({
    newPassword: '',
    confirmNewPassword: '',
    currentPassword: '',
    agreeToTerms,
    logoutOtherDevices: false
});

const ChangePasswordCard = (props) => {

    const {
        mode = 'dark',
        onSubmit,
        mainTitle,
        subtitle,
        showAgreeToTerms = false,
        initialAgreeToTerms = false,
        onAgreeToTermsChange,
        usePasswordRules = false,
        useCurrentPassword = false,
        useLogoutOtherDevices = false,
        passwordReset,
        isSignup = false
    } = props;

    const {
        FieldRow,
        FormLayout,
        OverrideStyles,
        PasswordRequirementsList,
        StyledButton,
        StyledCheckbox
    } = internals;
    const defaultValues = internals.buildDefaultValues({
        agreeToTerms: initialAgreeToTerms
    });

    // ** States
    const [state, setState] = useState({
        showNewPassword: false,
        showConfirmNewPassword: false,
        showCurrentPassword: false,
        privacyPolicyOpen: false,
        tosOpen: false,
        showForgotPasswordModal: false
    });

    // ** Hooks
    const theme = useTheme();
    const dispatch = useDispatch();

    const schema = buildChangePasswordSchema({
        usePasswordRules,
        showAgreeToTerms,
        useCurrentPassword,
        useLogoutOtherDevices
    });

    const formConfig = ChangePasswordFormConfig.buildFormConfig({
        onSubmit: onPasswordFormSubmit,
        defaultValues,
        schema
    });

    const {
        methods,
        renderForm
    } = useForm(formConfig);

    const {
        reset,
        control,
        formState: { errors, isSubmitting },
        watch,
        setError
    } = methods;

    const handleClickShowNewPassword = () => {

        setState({ ...state, showNewPassword: !state.showNewPassword });
    };

    const handleMouseDownNewPassword = (event) => {

        event.preventDefault();
    };

    const handleClickShowCurrentPassword = () => {

        setState({ ...state, showCurrentPassword: !state.showCurrentPassword });
    };

    const handleMouseDownCurrentPassword = (event) => {

        event.preventDefault();
    };

    const handleClickShowConfirmNewPassword = () => {

        setState({ ...state, showConfirmNewPassword: !state.showConfirmNewPassword });
    };

    const handleMouseDownConfirmNewPassword = (event) => {

        event.preventDefault();
    };

    async function onPasswordFormSubmit(data) {

        try {
            await onSubmit(data);
            reset(defaultValues);
            await dispatch(context.actions.alerts.notification.push({
                message: isSignup ?
                    'Password Updated Successfully'
                    : 'Password Changed Successfully'
            }));
        }
        catch (e) {

            //FIXME there is a bug with this catching a bug it works locally but on the staging it doesn't catch an error
            console.log({
                e
            });

            if (e?.response?.data?.reason === 'sameAsOldPassword') {

                setError('newPassword', { type: 'custom', message: 'New password must be different than the current password' },{ shouldFocus: true });
            }
            else if (e?.response?.data?.reason === 'wrongCurrent') {

                setError('currentPassword', { type: 'custom', message: 'Incorrect current password' },{ shouldFocus: true });
            }
            else if (e?.response?.data?.reason === 'oneOfOldPasswords') {

                setError('newPassword', { type: 'custom', message: 'New password can\'t be one of your old passwords' },{ shouldFocus: true });
            }
        }
    }

    const openPrivacyPolicy = (ev) => {

        ev.preventDefault();
        setState({ ...state, privacyPolicyOpen: true });
    };

    const closePrivacyPolicy = () => {

        setState({ ...state, privacyPolicyOpen: false });
    };

    const openTos = (ev) => {

        ev.preventDefault();
        setState({ ...state, tosOpen: true });
    };

    const closeTos = () => {

        setState({ ...state, tosOpen: false });
    };

    const escListener = (evt) => {

        if (evt.key === 'Escape') {
            setState({ ...state, showForgotPasswordModal: false });
        }
    };

    const forgotPwContentsRef = useCallback((node) => {

        if (node && !internals.initFocus) {
            internals.initPwContentsFocus = true;
            node.focus();
        }
    }, []);

    useEffect(() => {

        if (!state.showForgotPasswordModal) {
            internals.initPwContentsFocus = false;
        }
    }, [state.showForgotPasswordModal]);

    const currentPassword = watch('currentPassword');
    const newPassword = watch('newPassword');
    const confirmPassword = watch('confirmNewPassword');
    const agreeToTermsValue = watch('agreeToTerms');

    const uppercaseRegExp   = /(?=.*?[A-Z])/;
    const lowercaseRegExp   = /(?=.*?[a-z])/;
    const digitsOrSpecialRegExp = /(?=.*?[0-9\-!"#$%&()*,./:;?@[\]^_`{|}~+<=>]+)/;
    const minLengthRegExp   = /.{8,}/;

    const upperCaseMatched = usePasswordRules ? uppercaseRegExp.test(newPassword) : true;
    const lowerCaseMatched = usePasswordRules ? lowercaseRegExp.test(newPassword) : true;
    const digitsOrSpecialMatched = usePasswordRules ? digitsOrSpecialRegExp.test(newPassword) : true;
    const minLengthMatched = usePasswordRules ? minLengthRegExp.test(newPassword) : true;

    const passwordsMatch = newPassword === confirmPassword;

    return (
        <Card
            sx={{
                backgroundColor: mode === 'dark' ? 'primary.main' : 'background.paper',
                border: 'none'
            }}
            variant={'outlined'}
        >
            <CardHeader
                title={mainTitle}
                titleTypographyProps={{
                    component: 'h2',
                    style: {
                        paddingTop: isSignup ? 0 : 15,
                        fontSize: 28,
                        fontWeight: 'bold'
                    }
                }}
                sx={{
                    color: mode === 'dark' ? 'primary.contrastText' : 'text.primary',
                    textAlign: isSignup ? 'center' : 'left',
                    padding: {
                        xs: 0.5,
                        md: 1.5
                    },
                    '&:last-child':{
                        paddingBottom: {
                            xs: 0,
                            md: 2.5
                        }
                    }
                }}
                subheader={<Typography>{subtitle}</Typography>}
            />
            <CardContent mt={0} pt={0} sx={{
                padding:{
                    xs: 0.5,
                    md: 1.5
                },
                '&:last-child':{
                    paddingBottom:{
                        xs: 0,
                        md: 2.5
                    }
                }
            }}>
                {renderForm(
                    <OverrideStyles>
                        <FormLayout>
                            {useCurrentPassword ?  <FieldRow>
                                <FormControl fullWidth>
                                    <Controller
                                        name='currentPassword'
                                        control={control}
                                        rules={{ required: true }}
                                        // eslint-disable-next-line hapi/hapi-scope-start
                                        render={({ field }) => (

                                            <LoginTextField
                                                {...field}
                                                hasLightBackground={mode !== 'dark'}
                                                label='Current Password'
                                                id='input-current-password'
                                                error={Boolean(errors.currentPassword)}
                                                type={state.showCurrentPassword ? 'text' : 'password'}
                                                InputProps={{
                                                    endAdornment:<InputAdornment position='end'>
                                                        <IconButton
                                                            aria-label={state.showCurrentPassword ? 'hide current password' : 'show current password'}
                                                            aria-pressed={state.showCurrentPassword}
                                                            onClick={handleClickShowCurrentPassword}
                                                            onMouseDown={handleMouseDownCurrentPassword}
                                                            data-focus-outline='radius:40'
                                                        >
                                                            {state.showCurrentPassword && <VisibilityIcon htmlColor={mode === 'dark' ? theme.palette.primary.contrastText : theme.palette.text.secondary} />}
                                                            {!state.showCurrentPassword && <VisibilityOffIcon htmlColor={mode === 'dark' ? theme.palette.primary.contrastText : theme.palette.text.secondary} />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                }}
                                            />
                                        )}
                                    />
                                    {errors.currentPassword && (
                                        <FormHelperText sx={{ color: 'error.main' }}>{`${errors.currentPassword.message}`}</FormHelperText>
                                    )}
                                </FormControl>
                            </FieldRow> : null}
                            <FieldRow>
                                <FormControl fullWidth>
                                    <Controller
                                        name='newPassword'
                                        control={control}
                                        rules={{ required: true }}
                                        // eslint-disable-next-line hapi/hapi-scope-start
                                        render={({ field }) => (

                                            <LoginTextField
                                                required
                                                {...field}
                                                hasLightBackground={mode !== 'dark'}
                                                label='New Password'
                                                id='input-new-password'
                                                error={Boolean(errors.newPassword)}
                                                type={state.showNewPassword ? 'text' : 'password'}
                                                inputProps={usePasswordRules ? { 'aria-describedby': 'new-password-requirements-heading' } : undefined}
                                                InputProps={{
                                                    endAdornment:<InputAdornment position='end'>
                                                        <IconButton
                                                            aria-label={state.showNewPassword ? 'hide new password' : 'show new password'}
                                                            aria-pressed={state.showNewPassword}
                                                            onClick={handleClickShowNewPassword}
                                                            onMouseDown={handleMouseDownNewPassword}
                                                            data-focus-outline='radius:40'
                                                        >
                                                            {state.showNewPassword && <VisibilityIcon htmlColor={mode === 'dark' ? theme.palette.primary.contrastText : theme.palette.text.secondary} />}
                                                            {!state.showNewPassword && <VisibilityOffIcon htmlColor={mode === 'dark' ? theme.palette.primary.contrastText : theme.palette.text.secondary} />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                }}
                                            />
                                        )}
                                    />
                                    {errors.newPassword && (
                                        <FormHelperText sx={{ color: 'error.main' }}>{`${errors.newPassword.message}`}</FormHelperText>
                                    )}
                                </FormControl>
                            </FieldRow>
                            <FieldRow>
                                <FormControl fullWidth>
                                    <Controller
                                        name='confirmNewPassword'
                                        control={control}
                                        rules={{ required: true }}
                                        // eslint-disable-next-line hapi/hapi-scope-start
                                        render={({ field }) => (

                                            <LoginTextField
                                                required
                                                {...field}
                                                hasLightBackground={mode !== 'dark'}
                                                label='Confirm New Password'
                                                id='input-confirm-new-password'
                                                error={Boolean(errors.confirmNewPassword)}
                                                type={state.showConfirmNewPassword ? 'text' : 'password'}
                                                InputProps={{
                                                    endAdornment:<InputAdornment position='end'>
                                                        <IconButton
                                                            aria-label={state.showConfirmNewPassword ? 'hide confirm new password' : 'show confirm new password'}
                                                            aria-pressed={state.showConfirmNewPassword}
                                                            onClick={handleClickShowConfirmNewPassword}
                                                            onMouseDown={handleMouseDownConfirmNewPassword}
                                                            data-focus-outline='radius:40'
                                                        >
                                                            {state.showConfirmNewPassword && <VisibilityIcon htmlColor={mode === 'dark' ? theme.palette.primary.contrastText : theme.palette.text.secondary} />}
                                                            {!state.showConfirmNewPassword && <VisibilityOffIcon htmlColor={mode === 'dark' ? theme.palette.primary.contrastText : theme.palette.text.secondary} />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                }}
                                            />
                                        )}
                                    />
                                    {errors.confirmNewPassword && (
                                        <FormHelperText sx={{ color: 'error.main' }}>{`${errors.confirmNewPassword.message}`}</FormHelperText>
                                    )}
                                </FormControl>
                            </FieldRow>
                            {usePasswordRules ? <FieldRow>
                                <Typography
                                    id='new-password-requirements-heading'
                                    sx={{
                                        mt: 1,
                                        color: mode === 'dark' ? 'primary.contrastText' : 'text.primary',
                                        textAlign: 'left',
                                        fontSize:'.875rem'
                                    }}
                                >
                                    Password Requirements:
                                </Typography>
                                <PasswordRequirementsList>
                                    <ListItem tabIndex={-1}>
                                        <ListItemIcon>
                                            {!upperCaseMatched && <ClearIcon aria-hidden="true" htmlColor={mode === 'dark' ? theme.palette.primary.contrastText : theme.palette.text.primary} />}
                                            {upperCaseMatched && <CheckCircleIcon aria-hidden="true" htmlColor={mode === 'dark' ? 'rgb(76, 255, 80)' : theme.palette.success.main} />}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <>
                                                    <span aria-hidden="true">At least 1 uppercase letter</span>
                                                    <span role="status" style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>
                                                        {upperCaseMatched ? 'At least 1 uppercase letter: satisfied' : 'At least 1 uppercase letter: not satisfied'}
                                                    </span>
                                                </>
                                            }
                                            primaryTypographyProps={{
                                                sx:{
                                                    color: upperCaseMatched ? (mode === 'dark' ? 'rgb(76, 255, 80)' : 'success.main') : mode === 'dark' ? 'text.contrastText' : 'text.primary',
                                                    fontSize:{
                                                        xs: '0.875rem',
                                                        sm: '1rem'
                                                    }
                                                }
                                            }}
                                        />
                                    </ListItem>
                                    <ListItem tabIndex={-1}>
                                        <ListItemIcon>
                                            {!lowerCaseMatched && <ClearIcon aria-hidden="true" htmlColor={mode === 'dark' ? theme.palette.primary.contrastText : theme.palette.text.primary} />}
                                            {lowerCaseMatched && <CheckCircleIcon aria-hidden="true" htmlColor={mode === 'dark' ? 'rgb(76, 255, 80)' : theme.palette.success.main} />}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <>
                                                    <span aria-hidden="true">At least 1 lowercase letter</span>
                                                    <span role="status" style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>
                                                        {lowerCaseMatched ? 'At least 1 lowercase letter: satisfied' : 'At least 1 lowercase letter: not satisfied'}
                                                    </span>
                                                </>
                                            }
                                            primaryTypographyProps={{
                                                sx:{
                                                    color: lowerCaseMatched ? (mode === 'dark' ? 'rgb(76, 255, 80)' : 'success.main') : mode === 'dark' ? 'text.contrastText' : 'text.primary',
                                                    fontSize:{
                                                        xs: '0.875rem',
                                                        sm: '1rem'
                                                    }
                                                }
                                            }}
                                        />
                                    </ListItem>
                                    <ListItem tabIndex={-1}>
                                        <ListItemIcon>
                                            {!digitsOrSpecialMatched && <ClearIcon aria-hidden="true" htmlColor={mode === 'dark' ? theme.palette.primary.contrastText : theme.palette.text.primary} />}
                                            {digitsOrSpecialMatched && <CheckCircleIcon aria-hidden="true" htmlColor={mode === 'dark' ? 'rgb(76, 255, 80)' : theme.palette.success.main} />}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <>
                                                    <span aria-hidden="true">At least 1 numeral or special character</span>
                                                    <span role="status" style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>
                                                        {digitsOrSpecialMatched ? 'At least 1 numeral or special character: satisfied' : 'At least 1 numeral or special character: not satisfied'}
                                                    </span>
                                                </>
                                            }
                                            primaryTypographyProps={{
                                                sx: {
                                                    color: digitsOrSpecialMatched ? (mode === 'dark' ? 'rgb(76, 255, 80)' : 'success.main') : mode === 'dark' ? 'text.contrastText' : 'text.primary',
                                                    fontSize: {
                                                        xs: '0.875rem',
                                                        sm: '1rem'
                                                    }
                                                }
                                            }}
                                        />
                                    </ListItem>
                                    <ListItem tabIndex={-1}>
                                        <ListItemIcon>
                                            {!minLengthMatched && <ClearIcon aria-hidden="true" htmlColor={mode === 'dark' ? theme.palette.primary.contrastText : theme.palette.text.primary} />}
                                            {minLengthMatched && <CheckCircleIcon aria-hidden="true" htmlColor={mode === 'dark' ? 'rgb(76, 255, 80)' : theme.palette.success.main} />}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <>
                                                    <span aria-hidden="true">At least 8 characters total</span>
                                                    <span role="status" style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 }}>
                                                        {minLengthMatched ? 'At least 8 characters total: satisfied' : 'At least 8 characters total: not satisfied'}
                                                    </span>
                                                </>
                                            }
                                            primaryTypographyProps={{
                                                sx: {
                                                    color: minLengthMatched ? (mode === 'dark' ? 'rgb(76, 255, 80)' : 'success.main') : mode === 'dark' ? 'text.contrastText' : 'text.primary',
                                                    fontSize: {
                                                        xs: '0.875rem',
                                                        sm: '1rem'
                                                    }
                                                }
                                            }}
                                        />
                                    </ListItem>
                                </PasswordRequirementsList>
                            </FieldRow> : null}
                            {showAgreeToTerms ? <>
                                <FieldRow style={{ padding: '15px' }}>
                                    <FormControl>
                                        <Controller
                                            name='agreeToTerms'
                                            control={control}
                                            rules={{ required: true }}
                                            // eslint-disable-next-line hapi/hapi-scope-start
                                            render={({ field }) => (

                                                <FormControlLabel
                                                    required
                                                    label={
                                                        <Typography
                                                            sx={{
                                                                textAlign:'center',
                                                                color: mode === 'dark' ? theme.palette.text.contrastText : theme.palette.text.primary
                                                            }}
                                                        >
                                                            I agree with Nearpeer{'\''}s <a className={Classes.footerLink} href='' onClick={openTos} data-focus-outline='padding:3,radius:3'>Terms of Service</a> and <a className={Classes.footerLink} href='' onClick={openPrivacyPolicy} data-focus-outline='padding:3,radius:3'>Privacy Policy</a>.
                                                        </Typography>
                                                    }
                                                    sx={errors.agreeToTerms ? { '& .MuiTypography-root': { color: 'error.main' } } : null}
                                                    control={
                                                        <StyledCheckbox
                                                            {...field}
                                                            mode={mode}
                                                            checked={!!field.value}
                                                            onChange={(e) => {

                                                                field.onChange(e.target.checked);
                                                                onAgreeToTermsChange?.(e.target.checked);
                                                            }}
                                                            size='small'
                                                            name='agree-to-terms-checkbox'
                                                            sx={errors.agreeToTerms ? { color: 'error.main' } : null}
                                                            inputProps={{ 'data-focus-outline': 'radius:40', role: 'checkbox' }}
                                                        />
                                                    }
                                                />
                                            )}
                                        />
                                        {errors.agreeToTerms && (
                                            <FormHelperText sx={{ color: 'error.main' }} id='agree-to-terms-checkbox'>
                                                Please agree to terms
                                            </FormHelperText>
                                        )}
                                    </FormControl>
                                </FieldRow>
                                <FullScreenDialog
                                    title='Privacy Policy'
                                    open={state.privacyPolicyOpen}
                                    onRequestClose={closePrivacyPolicy}
                                >
                                    <PrivacyPolicyContents />
                                </FullScreenDialog>
                                <FullScreenDialog
                                    title='Terms of Service'
                                    open={state.tosOpen}
                                    onRequestClose={closeTos}
                                >
                                    <TosContents />
                                </FullScreenDialog>
                            </> : null}
                            {useLogoutOtherDevices ? <>
                                <FieldRow style={{ textAlign: 'left' }}>
                                    <FormControl>
                                        <Controller
                                            name='logoutOtherDevices'
                                            control={control}
                                            rules={{ required: true }}
                                            // eslint-disable-next-line hapi/hapi-scope-start
                                            render={({ field }) => (

                                                <FormControlLabel
                                                    label={
                                                        <Typography
                                                            sx={{
                                                                textAlign:'center',
                                                                color: mode === 'dark' ? theme.palette.text.contrastText : theme.palette.text.primary
                                                            }}
                                                        >
                                                            Log me out from all other devices
                                                        </Typography>
                                                    }
                                                    sx={errors.logoutOtherDevices ? { '& .MuiTypography-root': { color: 'error.main' } } : null}
                                                    control={
                                                        <StyledCheckbox
                                                            {...field}
                                                            mode={mode}
                                                            checked={!!field.value}
                                                            onChange={(e) => field.onChange(e.target.checked)}
                                                            size='small'
                                                            name='logoutOtherDevices-checkbox'
                                                            sx={errors.logoutOtherDevices ? { color: 'error.main' } : null}
                                                            style={{ marginLeft: 8 }}
                                                            inputProps={{ role: 'checkbox' }}
                                                        />
                                                    }
                                                />
                                            )}
                                        />
                                    </FormControl>
                                </FieldRow>
                            </> : null}
                            <FieldRow>
                                <StyledButton
                                    style={!usePasswordRules ? { marginTop: 20 } : {}}
                                    mode={mode}
                                    type='submit'
                                    disabled={isSubmitting || (
                                        (useCurrentPassword && !currentPassword)
                                        || !passwordsMatch
                                        || !upperCaseMatched
                                        || !lowerCaseMatched
                                        || !digitsOrSpecialMatched
                                        || !minLengthMatched
                                        || (showAgreeToTerms && !agreeToTermsValue)
                                    )}
                                    data-focus-outline='radius:40,padding:4'
                                    onClick={() => {

                                        FocusIndicator.onBlurHandler();
                                    }}
                                >
                                    {isSubmitting ? (
                                        <CircularProgress
                                            sx={{
                                                color: 'common.white',
                                                width: '20px !important',
                                                height: '20px !important',
                                                mr: theme.spacing(2)
                                            }}
                                        />
                                    ) : null}
                                    Save Password
                                </StyledButton>
                            </FieldRow>
                            {passwordReset && <FieldRow style={{ height: 30 }}>
                                <a
                                    onClick={() => setState({ ...state, showForgotPasswordModal: true })}
                                    onKeyDown={(evt) => {

                                        if (evt.key === 'Enter' || evt.code === 'Space') {
                                            setState({ ...state, showForgotPasswordModal: true });
                                        }
                                    }}
                                    className={Classes.link}
                                    tabIndex={0}
                                    data-focus-outline='padding:4,radius:4'
                                >
                                    Forgot Current Password
                                </a>
                            </FieldRow>}
                        </FormLayout>
                    </OverrideStyles>
                )}
            </CardContent>
            <Dialog
                open={state.showForgotPasswordModal}
                disableEscapeKeyDown
                onClose={() => setState({ ...state, showForgotPasswordModal: false })}
                aria-labelledby='alert-password-reset-title'
                aria-describedby='alert--password-reset-description'
            >
                <div
                    tabIndex={0}
                    ref={forgotPwContentsRef}
                    data-focus-outline='radius:15'
                    onKeyDown={escListener}
                >
                    <DialogTitle id='alert-Inappropriate-Message-title'>Password Reset</DialogTitle>
                    <DialogContent>
                        <DialogContentText id='alert-Inappropriate-Message-description'>
                            {'Clicking \'Confirm\' will send you a password reset email and log you out on all devices. If you haven\'t received this email within a few minutes, please check your spam folder.'}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setState({ ...state, showForgotPasswordModal: false })}
                            color='secondary'
                            data-focus-outline='radius:40'
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {

                                analyticsTemplates.buttons('change_password', 'change_password: forgot password');
                                // passwordReset func logs the user out
                                passwordReset();
                            }}
                            color='primary'
                            data-focus-outline='radius:40'
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </div>
            </Dialog>
        </Card>
    );
};

ChangePasswordCard.propTypes = {
    mode: T.string,
    useCurrentPassword: T.bool,
    useLogoutOtherDevices: T.bool,
    onSubmit: T.func.isRequired,
    mainTitle: T.string.isRequired,
    subtitle: T.string.isRequired,
    showAgreeToTerms: T.bool,
    initialAgreeToTerms: T.bool,
    onAgreeToTermsChange: T.func,
    usePasswordRules: T.bool,
    passwordReset: T.func,
    isSignup: T.bool
};

module.exports = ChangePasswordCard;

internals.OverrideStyles = Styled.div`
    .MuiFormControlLabel-asterisk {
        display: none;
    }
    .MuiFormLabel-asterisk {
        display: none;
    }
`;

internals.FormLayout = Styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 0;
`;

internals.FieldRow = Styled.div`
    width: 100%;
`;

internals.PasswordRequirementsList = Styled.ul`
    padding-left: 0;
    margin: 0;

    li {
        margin-bottom: 0;
    }
`;

internals.StyledButton = Styled(Button)`
    background-color: ${({ theme,mode }) => mode === 'dark' ? theme.palette.primary.contrastText  : theme.palette.primary.main};
    color: ${({ theme,mode }) => mode === 'dark' ? theme.palette.primary.main  : theme.palette.primary.contrastText};
    height: 3em;
    width: 100%;
    padding: 0;
    &:hover {
        background-color: ${({ theme,mode }) => mode === 'dark' ? theme.palette.primary.contrastText  : theme.palette.primary.main};
        color: ${({ theme,mode }) => mode === 'dark' ? theme.palette.primary.main  : theme.palette.primary.contrastText};
    }
    &.Mui-disabled{
        background-color: ${({ theme,mode }) => mode === 'dark' ? theme.palette.primary.contrastText  : theme.palette.action.disabledBackground};
        color: ${({ theme }) => theme.palette.action.disabled};
    }
    font-family: ${Fonts.headerFont};
    font-weight: bold;
    text-transform: none;
    font-size: 1rem;
    line-height: 3em;
`;

internals.StyledCheckbox = Styled(Checkbox)`
    &&& {
        color: ${({ theme, mode }) => mode === 'dark' ? theme.palette.common.white : theme.palette.primary.main};
        fill: ${({ theme, mode }) => mode === 'dark' ? theme.palette.common.white : theme.palette.primary.main};
    }
`;
